/**
 * @module DetailPago
 * @description Colección de componentes de presentación para detalles de pago.
 * Incluye componentes para mostrar información personal, resumen de pago
 * y resultados de transacciones ePayco.
 *
 * Componentes exportados:
 * - DetailPago: Detalle de pago en formato compacto (2 columnas)
 * - DetailPago2: Detalle de pago en formato vertical (1 columna, rounded-pill)
 * - DetailPago3: Datos personales del titular con máscara de privacidad
 * - ResumenPago: Resumen del pago seleccionado (servicio, referencia, vencimiento, total)
 * - ResultadoTransaccion: Detalle de la respuesta de ePayco tras el pago
 */

import { FC } from "react";
import {
  MoneyMask,
  EmailMask,
  numberMask,
  valortotalDocumentosSeleccionados,
} from "../../helpers";
import { Iestado, respuestaEpayco } from "../../helpers/dataExamples";

/** Props para DetailPago y DetailPago2: datos de un pago individual */
type props = {
  /** Descripción o nombre del servicio de pago */
  descrip: string;
  /** Fecha límite de pago */
  limite: string;
  /** Valor monetario a pagar */
  valor: string | number;
  /** Correo electrónico del titular */
  correo: string;
  /** Teléfono de contacto */
  telefono: string;
  /** Nombres del titular */
  nombres: string;
  /** Apellidos del titular */
  apellidos: string;
};

/** Props para componentes que consumen el estado global del flujo de pagos */
type props2 = {
  /** Estado completo del módulo de pagos */
  estado: Iestado;
};

/** Props para el componente de resultado de transacción ePayco */
type props3 = {
  /** Objeto con todos los campos de respuesta de la transacción ePayco */
  resultadoPago: respuestaEpayco;
};

/**
 * DetailPago - Detalle de pago en formato compacto con campos de solo lectura.
 * Layout de 2 columnas para límite/valor, correo/teléfono, nombres/apellidos.
 * Usado para vistas de escritorio donde se aprovecha el ancho disponible.
 */
const DetailPago: FC<props> = ({
  descrip,
  limite,
  valor,
  correo,
  telefono,
  nombres,
  apellidos,
}) => {
  return (
    <>
      <div className="row mt-3">
        <div className="col">
          <label className="etiqueta_oculta">Descripción pago</label>
          <input
            type="text"
            className="form-control form-control-solid form-control-sm"
            id="descripcion"
            maxLength={20}
            placeholder="Referencia de Pago"
            value={descrip}
          ></input>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col">
          <label className="etiqueta_oculta">limite pago</label>
          <input
            type="text"
            className="form-control form-control-solid form-control-sm"
            id="descripcion"
            maxLength={20}
            placeholder="Referencia de Pago"
            value={limite}
            readOnly
          ></input>
        </div>

        <div className="col">
          <label className="etiqueta_oculta">Valor a pagar</label>
          <input
            type="text"
            className="form-control form-control-solid form-control-sm"
            id="descripcion"
            maxLength={20}
            placeholder="Referencia de Pago"
            value={valor}
            readOnly
          ></input>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col">
          <label className="etiqueta_oculta">Correo electrónico</label>
          <input
            type="text"
            className="form-control form-control-solid form-control-sm"
            id="descripcion"
            maxLength={20}
            placeholder="Referencia de Pago"
            value={correo}
            readOnly
          ></input>
        </div>

        <div className="col">
          <label className="etiqueta_oculta">Telefono</label>
          <input
            type="text"
            className="form-control form-control-solid form-control-sm"
            id="descripcion"
            maxLength={20}
            placeholder="Referencia de Pago"
            value={telefono}
            readOnly
          ></input>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col">
          <label className="etiqueta_oculta">Nombres</label>
          <input
            type="text"
            className="form-control form-control-solid form-control-sm"
            id="descripcion"
            maxLength={20}
            placeholder="Referencia de Pago"
            value={nombres}
            readOnly
          ></input>
        </div>

        <div className="col">
          <label className="etiqueta_oculta">Apellidos</label>
          <input
            type="text"
            className="form-control form-control-solid form-control-sm"
            id="descripcion"
            maxLength={20}
            placeholder="Referencia de Pago"
            value={apellidos}
            readOnly
          ></input>
        </div>
      </div>
    </>
  );
};

/**
 * DetailPago2 - Detalle de pago en formato vertical con estilo rounded-pill.
 * Cada campo ocupa una fila completa. Diseño optimizado para vistas móviles
 * o contextos donde se prefiere lectura lineal.
 */
const DetailPago2: FC<props> = ({
  descrip,
  limite,
  valor,
  correo,
  telefono,
  nombres,
  apellidos,
}) => {
  return (
    <>
      <div className="row mt-3">
        <div className="col">
          <label className="etiqueta_oculta">Descripción pago</label>
          <input
            type="text"
            className="form-control form-control-solid rounded-pill"
            id="descripcion"
            maxLength={20}
            placeholder="Referencia de Pago"
            value={descrip}
            readOnly
          ></input>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col">
          <label className="etiqueta_oculta">limite pago</label>
          <input
            type="text"
            className="form-control form-control-solid rounded-pill"
            id="descripcion"
            maxLength={20}
            placeholder="Referencia de Pago"
            value={limite}
            readOnly
          ></input>
        </div>
      </div>
      <div className="row mt-3">
        <div className="col">
          <label className="etiqueta_oculta">Valor a pagar</label>
          <input
            type="text"
            className="form-control form-control-solid rounded-pill"
            id="descripcion"
            maxLength={20}
            placeholder="Referencia de Pago"
            value={valor}
            readOnly
          ></input>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col">
          <label className="etiqueta_oculta">Correo electrónico</label>
          <input
            type="text"
            className="form-control form-control-solid rounded-pill"
            id="descripcion"
            maxLength={20}
            placeholder="Referencia de Pago"
            value={correo}
            readOnly
          ></input>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col">
          <label className="etiqueta_oculta">Telefono</label>
          <input
            type="text"
            className="form-control form-control-solid rounded-pill"
            id="descripcion"
            maxLength={20}
            placeholder="Referencia de Pago"
            value={telefono}
            readOnly
          ></input>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col">
          <label className="etiqueta_oculta">Nombres</label>
          <input
            type="text"
            className="form-control form-control-solid rounded-pill"
            id="descripcion"
            maxLength={20}
            placeholder="Referencia de Pago"
            value={nombres}
            readOnly
          ></input>
        </div>
      </div>
      <div className="row mt-3">
        <div className="col">
          <label className="etiqueta_oculta">Apellidos</label>
          <input
            type="text"
            className="form-control form-control-solid rounded-pill"
            id="descripcion"
            maxLength={20}
            placeholder="Referencia de Pago"
            value={apellidos}
            readOnly
          ></input>
        </div>
      </div>
    </>
  );
};

/**
 * DetailPago3 - Datos personales del titular con máscaras de privacidad.
 * Aplica EmailMask al correo y numberMask al teléfono para ocultar parcialmente
 * la información sensible. Campos deshabilitados (solo lectura visual).
 */
const DetailPago3: FC<props2> = ({ estado }) => {
  /** Correo electrónico con caracteres enmascarados por privacidad */
  const email = EmailMask(estado.correo);
  return (
    <>
      <div className="row mt-3">
        <div className="col">
          {/*<label className='etiqueta_oculta'>Tipo de documento:</label>*/}
          <select
            className="form-select rounded-pill"
            id="select_tipo_doc"
            value={estado.tipoDocumento}
            disabled={true}
          >
            <option value="T.I">T.I - Tarjeta de Identidad</option>
            <option value="R.E">R.E - Registro Civil</option>
            <option value="NIT">NIT - NIT</option>
            <option value="C.E">C.E - Cedula de Extranjeria</option>
            <option value="C.C">C.C - Cedula de Ciudadania</option>
          </select>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col">
          {/*<label className='etiqueta_oculta'>Numero de documento:</label>*/}
          <input
            type="text"
            className="form-control form-control-lg rounded-pill"
            id="input_documento"
            maxLength={14}
            placeholder="Numero de documento"
            value={estado.documento}
            disabled={true}
          ></input>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col">
          {/*<label className='etiqueta_oculta'>Nombres</label>*/}
          <input
            type="text"
            className="form-control form-control-solid rounded-pill"
            id="descripcion"
            maxLength={20}
            placeholder="Nombres"
            value={estado.nombres}
            readOnly
          ></input>
        </div>
      </div>
      <div className="row mt-3">
        <div className="col">
          {/*<label className='etiqueta_oculta'>Apellidos</label>*/}
          <input
            type="text"
            className="form-control form-control-solid rounded-pill"
            id="descripcion"
            maxLength={20}
            placeholder="Apellidos"
            value={estado.apellidos}
            readOnly
          ></input>
        </div>
      </div>
      <div className="row mt-3">
        <div className="col">
          {/*<label className="etiqueta_oculta">Correo electrónico</label>*/}
          <input
            type="text"
            className="form-control form-control-solid rounded-pill"
            id="descripcion"
            maxLength={20}
            placeholder="Email"
            value={email}
            readOnly
          ></input>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col">
          {/*<label className="etiqueta_oculta">Telefono</label>*/}
          <input
            type="text"
            className="form-control form-control-solid rounded-pill"
            id="descripcion"
            maxLength={20}
            placeholder="Telefono"
            value={numberMask(estado.telefono)}
            readOnly
          ></input>
        </div>
      </div>
    </>
  );
};

/**
 * ResumenPago - Componente de resumen de pago antes de la confirmación.
 * Muestra el servicio seleccionado, la referencia activa, la fecha de vencimiento
 * y el total a pagar calculado según la modalidad elegida (mínimo/total/abono).
 * El monto se formatea con separadores de miles usando MoneyMask.
 */
const ResumenPago: FC<props2> = ({ estado }) => {
  /** Total a pagar calculado según documentos seleccionados y modalidad de pago */
  let money = valortotalDocumentosSeleccionados(estado);
  /** Referencia actualmente seleccionada (clave del diccionario de referencias) */
  let refActiva = estado.referenciaActiva;
  return (
    <div id="resumenDelPago" className="m-4">
      <div className="row">
        <div className="col">
          <h5>Pago de servicios</h5>
          <p>
            {refActiva === ""
              ? ""
              : estado.referencias[estado.referenciaActiva].descrip}
          </p>
        </div>
      </div>

      <div className="row">
        <div className="col">
          <h5>Referencia de pago</h5>
          <p>{estado.referenciaActiva}</p>
        </div>
      </div>

      <div className="row">
        <div className="col">
          <h5>Vence</h5>
          <p>
            {refActiva === ""
              ? ""
              : estado.referencias[estado.referenciaActiva].limite}
          </p>
        </div>
      </div>

      <div className="row">
        <div className="col">
          <h5>Total a pagar</h5>
          <p className="text-comfe text-end fs-1 fw-bolder">
            ${MoneyMask(money + "")}
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * ResultadoTransaccion - Tabla de detalle del resultado de la transacción ePayco.
 * Muestra fecha, descripción, referencias, método de pago, autorización,
 * recibo, documento y total. Incluye mensaje de éxito si la respuesta es "Aceptada".
 */
const ResultadoTransaccion: FC<props3> = ({ resultadoPago }) => {
  return (
    <>
      <div className="row mt-1 fs-5">
        <div className="col-md-6">Fecha de pago</div>
        <div className="col-md-6 text-end">
          {resultadoPago.x_transaction_date}
        </div>
      </div>
      <div className="row mt-1 fs-5">
        <div className="col-md-6">Descripción</div>
        <div className="col-md-6 text-end">{resultadoPago.x_description}</div>
      </div>
      <div className="row mt-1 fs-5">
        <div className="col-md-6">Ref. Comercio</div>
        <div className="col-md-6 text-end">{resultadoPago.x_id_factura}</div>
      </div>
      <div className="row mt-1 fs-5">
        <div className="col-md-6">Ref. Epayco</div>
        <div className="col-md-6 text-end">{resultadoPago.x_ref_payco}</div>
      </div>
      <div className="row mt-1 fs-5">
        <div className="col-md-6">Método de pago</div>
        <div className="col-md-6 text-end">
          {resultadoPago.x_franchise + " - " + resultadoPago.x_bank_name}
        </div>
      </div>
      <div className="row mt-1 fs-5">
        <div className="col-md-6">Autorización / CUS</div>
        <div className="col-md-6 text-end">{resultadoPago.x_approval_code}</div>
      </div>
      <div className="row mt-1 fs-5">
        <div className="col-md-6">Recibo</div>
        <div className="col-md-6 text-end">
          {resultadoPago.x_transaction_id}
        </div>
      </div>
      <div className="row mt-1 fs-5">
        <div className="col-md-6">Documento de pago</div>
        <div className="col-md-6 text-end">{resultadoPago.x_extra1}</div>
      </div>
      <div className="row mt-1 fs-5">
        <div className="col-md-6">Total</div>
        <div className="col-md-6 text-end">
          <b>${MoneyMask(resultadoPago.x_amount_ok + "")}</b>
        </div>
      </div>
      <div
        className={
          "row mt-10 fs-5 " +
          (resultadoPago.x_response === "Aceptada" ? "" : " d-none")
        }
      >
        <div className="col-md-12 text-center">
          <p>
            {/*Gracias, su compra se ha realizado con éxito y su orden ha sido
            procesada.*/}
            Gracias, su compra se ha realizado con éxito y su orden esta siendo
            procesada.
          </p>
        </div>
      </div>
    </>
  );
};

export {
  DetailPago,
  DetailPago2,
  DetailPago3,
  ResumenPago,
  ResultadoTransaccion,
};
